import React, {
  useState,
  useEffect,
  useRef,
  ChangeEventHandler,
  ReactNode,
} from "react";

import { TableHeaderType } from "~/models/types/TableHeaderType";
import { Pagination } from "../pagination/Pagination";
import { SIMPLTableFilter } from "./SIMPLTableFilter";
import ChipsPropertyScripts from "~/models/scripts/ChipsPropertyScripts";

import "../../../assets/styles/Table.css";

import { LABELS } from "~/resources/Labels";

import {
  CONTAINER_ITEM_CLASS,
  CONTAINER_ITEM_CLASS_ACTIONS,
} from "~/resources/grid";

import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";

import { Entries } from "~/models/enums/Entries";
import DecommissionStatus from "~/models/enums/DecommissionStatus.enum";

import { SelectSecondaryClasses } from "~/assets/styles/classes/ButtonClasses";

import { SIMPLPill } from "../pill/SIMPLPill";

import HourglassDisabledIcon from "@mui/icons-material/HourglassDisabled";

import { SIMPLTitle } from "../title/SIMPLTitle";
import { SIMPLTableHeader } from "./SimplTableHeader";

import {
  PropRow,
  SortTable,
} from "~/models/Commons ";

import { tabClass } from "~/assets/styles/classes/TableRowClasses";
import { t } from "i18next";

import { sortTableData } from "~/Utils/sortTableData";

interface Prop {
  tableHeaders: TableHeaderType[];
  rows: PropRow[];
  button?: React.ReactNode;
  sortConfig: SortTable;
  active: boolean;
  handleSort: (key: string) => void;
  chipLabels?: ChipsPropertyScripts[];
  filterActive: boolean;
  showHandler: () => void;
  handlerChips?: () => void;
  handleDecommision?: Function;
  handleRowClick?: Function;
  handleOpenDetail?: Function;
  handleOpenEdit?: Function;
  handleConfigFile?: Function;
  handleDelete?: Function;
  handleDownload?: Function;
  handleOpenCopy?: Function;
  handleOpenReviewOutputTemplate?: Function;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onChangePage: (page: number) => void;
  page: number;
  size: number;
  showPagination: boolean;
  showFilter?: boolean;
  showButtonDecommissionInModal?: boolean;
  showButtonDetailsInModal?: boolean;
  showButtonDownloadInModal?: boolean;
  showButtonDeleteInModal?: boolean;
  showConfigFileInModal?: boolean;
  showEditInModal?: boolean;
  showCopyInModal?: boolean;
  showButtonMoreActions?: boolean;
  clear: () => void;
  custumizedNoDataAvailable?: string;
  title: string;
  lastBorder?: string;
  tabs?: boolean;
  isActive?: boolean;
  setIsActive?: (active: boolean) => void;
  activeLabel?: string;
  inactiveLabel?: string;
  showReviewOutputTemplateInModal?: boolean;
  savedDraftIds?: Set<string>;
  handleOpenApplyOutputMessage?: (row: any) => void;
  defaultSortKey?: string;
}

const ModalOutsideCloser: React.FC<{
  children: ReactNode;
  onClose: () => void;
}> = ({ children, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ) => {
      if (
        ref.current &&
        !ref.current.contains(
          event.target as Node
        )
      ) {
        onClose();
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
  }, [onClose]);

  return (
    <div ref={ref}>
      {children}
    </div>
  );
};

export const SIMPLTable = (
  props: Prop
) => {
  const {
    custumizedNoDataAvailable =
      LABELS.NoData,

    page,
    size,

    showPagination,

    showButtonDecommissionInModal = true,
    showButtonDetailsInModal = true,
    showButtonDownloadInModal = true,
    showButtonDeleteInModal = true,
    showConfigFileInModal = true,
    showEditInModal = true,
    showCopyInModal = true,
    showReviewOutputTemplateInModal = true,
    savedDraftIds,
    handleOpenApplyOutputMessage,
    showButtonMoreActions = true,

    showFilter,
    chipLabels,
    filterActive,

    rows,
    active,
    tableHeaders,

    sortConfig,
    handleSort,
    defaultSortKey,

    button,
    showHandler,
    handlerChips,

    handleRowClick,
    handleDecommision,
    handleOpenDetail,
    handleOpenEdit,
    handleDownload,
    handleDelete,
    handleConfigFile,
    handleOpenCopy,
    handleOpenReviewOutputTemplate,

    onChange,
    onChangePage,
    clear,

    title,
    lastBorder,

    tabs,
    isActive,

    activeLabel = LABELS.Active,
    inactiveLabel = LABELS.Inactive,

    setIsActive,
  } = props;

  /**
   * Sort the original rows whenever
   * rows or sortConfig changes.
   *
   * IMPORTANT:
   * Sorting happens BEFORE pagination.
   */
  const sortedRows = React.useMemo(() => {
    return sortTableData(
      rows,
      sortConfig,
      defaultSortKey
    );
  }, [
    rows,
    sortConfig.key,
    sortConfig.direction,
    defaultSortKey,
  ]);

  /**
   * Calculate total pages based
   * on the sorted data.
   */
  const totalPages = Math.ceil(
    sortedRows.length / size
  );

  /**
   * Make sure the current page
   * remains valid after sorting/filtering.
   */
  useEffect(() => {
    if (
      totalPages > 0 &&
      page >= totalPages
    ) {
      onChangePage(
        totalPages - 1
      );
    }
  }, [
    page,
    totalPages,
    onChangePage,
  ]);

  /**
   * Pagination happens AFTER sorting.
   */
  const paginatedRows =
    React.useMemo(() => {
      const startIndex =
        page * size;

      const endIndex =
        startIndex + size;

      return sortedRows.slice(
        startIndex,
        endIndex
      );
    }, [
      sortedRows,
      page,
      size,
    ]);

  /**
   * Correct item range for footer.
   */
  const startItem =
    sortedRows.length === 0
      ? 0
      : page * size + 1;

  const endItem = Math.min(
    (page + 1) * size,
    sortedRows.length
  );

  const [
    currentRowId,
    setCurrentRowId,
  ] = useState<string | null>(null);

  const toggleModal = (
    rowId: string
  ) => {
    setCurrentRowId(
      currentRowId === rowId
        ? null
        : rowId
    );
  };

  /**
   * Supports nested values such as:
   * project.name
   */
  const getValue = (
    row: PropRow["row"],
    path: string
  ) =>
    path
      .split(".")
      .reduce(
        (acc: any, key) =>
          acc?.[key],
        row
      );

  const getDisplayValue = (
    row: any,
    id: string
  ) => {
    const value = getValue(
      row,
      id
    );

    if (
      (
        id === "projectId" ||
        id === "flavor" ||
        id === "scriptType"
      ) &&
      (
        value === null ||
        value === undefined ||
        value === ""
      )
    ) {
      return LABELS.Empty;
    }

    return value ?? null;
  };

  const hasActions = (
    row: PropRow["row"]
  ) =>
    showConfigFileInModal ||
    (
      showButtonDecommissionInModal &&
      row.resourceStatus ===
        DecommissionStatus.ACTIVATED
    ) ||
    showButtonDetailsInModal ||
    showEditInModal ||
    showButtonDownloadInModal ||
    showButtonDeleteInModal ||
    (showReviewOutputTemplateInModal && (
      row.resourceStatus === DecommissionStatus.PENDING_CREDENTIALS ||
      row.resourceStatus === DecommissionStatus.RENEWAL_CREDENTIALS ||
      row.resourceStatus === DecommissionStatus.AWAITING_PROVISIONING_INPUT ||
      row.resourceStatus === DecommissionStatus.AWAITING_RENEWAL_INPUT ||
      row.resourceStatus === DecommissionStatus.READY_TO_SEND
    )) ||
    (showReviewOutputTemplateInModal && savedDraftIds?.has(row.requesterUniqueId));

  return (
    <div className="w-[1293px] mx-auto">
      <div className="px-4 py-0">
        <SIMPLTitle
          title={title}
        />
      </div>

      <div className="flex justify-between items-center mb-4 px-4 py-2">
        {/* LEFT GROUP */}
        <div className="flex items-center space-x-4">
          {button}

          <div className="border-b border-gray-200">
            {tabs && (
              <nav className="flex space-x-2">
                {/* Active Tab */}
                <button
                  onClick={() =>
                    setIsActive?.(true)
                  }
                  className={tabClass(
                    isActive!
                  )}
                >
                  {activeLabel}
                </button>

                {/* Inactive Tab */}
                <button
                  onClick={() =>
                    setIsActive?.(false)
                  }
                  className={tabClass(
                    !isActive
                  )}
                >
                  {inactiveLabel}
                </button>
              </nav>
            )}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div>
          {showFilter ? (
            <SIMPLTableFilter
              clear={clear}
              chipLabels={
                chipLabels || []
              }
              filterAtive={
                filterActive
              }
              showHandler={
                showHandler
              }
              handlerChips={
                handlerChips
              }
            />
          ) : (
            <div className="btn-container" />
          )}
        </div>
      </div>

      <div className="px-4 py-2">
        <table className="w-[1293px] border-collapse">
          <SIMPLTableHeader
            headers={tableHeaders}
            showActionsHeader={active}
            sortConfig={sortConfig}
            handleSort={handleSort}
            lastBorder={lastBorder}
          />

          <tbody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map(
                (propRow) => {
                  const row =
                    propRow.row;

                  const rowId =
                    row.id ||
                    row.requesterUniqueId;

                  const isModalOpen =
                    currentRowId ===
                    rowId;

                  return (
                    <tr
                      key={rowId}
                      className="odd:bg-white even:bg-[#F5F5F5]"
                      onClick={() =>
                        handleRowClick?.(
                          row
                        )
                      }
                    >
                      {active && (
                        <td
                          className={`${CONTAINER_ITEM_CLASS_ACTIONS} text-center`}
                        >
                          <div className="flex items-center justify-center p-2">
                            {showButtonMoreActions &&
                              isActive! &&
                              hasActions(
                                row
                              ) && (
                                <div className="relative inline-block">
                                  <button
                                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
                                    onClick={(
                                      event
                                    ) => {
                                      event.stopPropagation();

                                      toggleModal(
                                        rowId
                                      );
                                    }}
                                  >
                                    <MoreVertOutlinedIcon fontSize="small" />
                                  </button>

                                  {isModalOpen && (
                                    <ModalOutsideCloser
                                      onClose={() =>
                                        toggleModal(
                                          rowId
                                        )
                                      }
                                    >
                                      <div className="absolute top-0 left-full -translate-x-6 w-40 bg-white border shadow-lg rounded-md z-50">
                                        {showConfigFileInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleConfigFile?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "AddConfig"
                                            )}
                                          </button>
                                        )}

                                        {showButtonDecommissionInModal &&
                                          row.resourceStatus ===
                                            DecommissionStatus.ACTIVATED && (
                                            <button
                                              className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                              onClick={() => {
                                                handleDecommision?.(
                                                  row
                                                );

                                                toggleModal(
                                                  rowId
                                                );
                                              }}
                                            >
                                              {t(
                                                "Decommission"
                                              )}
                                            </button>
                                          )}

                                        {showButtonDetailsInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenDetail?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "DetailView"
                                            )}
                                          </button>
                                        )}

                                        {showEditInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenEdit?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "Edit"
                                            )}
                                          </button>
                                        )}

                                        {showButtonDownloadInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleDownload?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "Download"
                                            )}
                                          </button>
                                        )}

                                        {showCopyInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenCopy?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "Copy"
                                            )}
                                          </button>
                                        )}

                                        {showButtonDeleteInModal && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleDelete?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "Delete"
                                            )}
                                          </button>
                                        )}

                                        {showReviewOutputTemplateInModal && (
                                          row.resourceStatus === DecommissionStatus.PENDING_CREDENTIALS ||
                                          row.resourceStatus === DecommissionStatus.RENEWAL_CREDENTIALS ||
                                          row.resourceStatus === DecommissionStatus.AWAITING_PROVISIONING_INPUT ||
                                          row.resourceStatus === DecommissionStatus.AWAITING_RENEWAL_INPUT ||
                                          row.resourceStatus === DecommissionStatus.READY_TO_SEND
                                        ) && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenReviewOutputTemplate?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "FillOutputMessageTitle"
                                            )}
                                          </button>
                                        )}

                                        {showReviewOutputTemplateInModal &&
                                          row.resourceStatus === DecommissionStatus.READY_TO_SEND && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenApplyOutputMessage?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "SendEmail"
                                            )}
                                          </button>
                                        )}

                                        {showReviewOutputTemplateInModal && (
                                          row.resourceStatus === DecommissionStatus.RENEWAL_CREDENTIALS ||
                                          savedDraftIds?.has(row.requesterUniqueId)
                                        ) && (
                                          <button
                                            className="w-full px-3 py-2 hover:bg-gray-100 text-left"
                                            onClick={() => {
                                              handleOpenApplyOutputMessage?.(
                                                row
                                              );

                                              toggleModal(
                                                rowId
                                              );
                                            }}
                                          >
                                            {t(
                                              "ApplyOutputMessage"
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </ModalOutsideCloser>
                                  )}
                                </div>
                              )}
                          </div>
                        </td>
                      )}

                      {tableHeaders.map(
                        (header, index) => {
                          const value =
                            getDisplayValue(
                              row,
                              header.id.toString()
                            );

                          const isLong =
                            typeof value ===
                              "string" &&
                            value.length > 80;

                          return (
                            <td
                              key={index}
                              className={`${CONTAINER_ITEM_CLASS} ${
                                isLong
                                  ? "truncate max-w-[660px]"
                                  : ""
                              }`}
                              title={
                                typeof value ===
                                "string"
                                  ? value
                                  : undefined
                              }
                            >
                              {header.id ===
                              "resourceStatus" ? (
                                <SIMPLPill
                                  label={row[header.id]}
                                  variant={row.resourceStatus}
                                />
                              ) : (
                                <div
                                  className={
                                    isLong
                                      ? "truncate"
                                      : ""
                                  }
                                >
                                  {value}
                                </div>
                              )}
                            </td>
                          );
                        }
                      )}
                    </tr>
                  );
                }
              )
            ) : (
              <tr className="h-[400px]">
                <td
                  colSpan={
                    tableHeaders.length +
                    (active ? 1 : 0)
                  }
                  className="text-center"
                >
                  <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                    <HourglassDisabledIcon className="w-12 h-12 mb-2" />

                    <span className="text-lg">
                      {
                        custumizedNoDataAvailable
                      }
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr>
              <td
                colSpan={
                  tableHeaders.length +
                  (active ? 1 : 0)
                }
              >
                {sortedRows.length > 0 &&
                  showPagination && (
                    <div className="flex justify-between items-center p-2">
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-1">
                          <p>
                            {t(
                              "ItemsPerPage"
                            )}
                          </p>

                          <select
                            name="number"
                            value={size}
                            onChange={onChange}
                            className={`w-[94px] ${SelectSecondaryClasses(
                              false
                            )}`}
                          >
                            {Object.values(
                              Entries
                            )
                              .filter(
                                (value) =>
                                  typeof value ===
                                  "number"
                              )
                              .map(
                                (value) => (
                                  <option
                                    key={
                                      value
                                    }
                                    value={
                                      value
                                    }
                                  >
                                    {
                                      value
                                    }
                                  </option>
                                )
                              )}
                          </select>
                        </div>

                        <p className="text-sm mt-1">
                          {t("Showing")}{" "}
                          {startItem}-
                          {endItem}{" "}
                          {t("Of")}{" "}
                          {
                            sortedRows.length
                          }
                        </p>
                      </div>

                      <Pagination
                        currentPage={page}
                        totalPages={
                          totalPages
                        }
                        onChangePage={
                          onChangePage
                        }
                      />
                    </div>
                  )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};